import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2, HeartHandshake } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import PtfButton from "@/components/PtfButton";
import { api, formatApiError } from "@/lib/api";
import { openCheckout } from "@/lib/payments";

const DEFAULT_PRESETS = [500, 1000, 2500, 5000];
const inputCls = "w-full bg-transparent border-b border-ptf-primary/25 py-3 text-base outline-none transition-colors duration-300 focus:border-ptf-secondary placeholder:text-ptf-text/40 text-ptf-text";

export default function ProgrammeDonate({ programme }) {
  const [open, setOpen] = useState(false);
  const presets = programme.donation_suggested_amounts?.length > 0 ? programme.donation_suggested_amounts : DEFAULT_PRESETS;
  const minAmount = programme.donation_min_amount || 100;
  const [amount, setAmount] = useState(presets[0]);
  const [custom, setCustom] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const finalAmount = custom ? Number(custom) : amount;

  const submit = async (e) => {
    e.preventDefault();
    if (!finalAmount || finalAmount < minAmount) { toast.error(`Minimum donation is \u20b9${minAmount.toLocaleString("en-IN")}.`); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/donations", { ...form, amount: finalAmount, frequency: "one-time", programme_id: programme.id });
      if (!data.payment) {
        setDone({ paid: false, message: data.message });
        return;
      }
      const resp = await openCheckout({
        keyId: data.key_id, orderId: data.order_id, amount: data.amount, currency: data.currency,
        description: `Donation to ${programme.name}`,
        prefill: { name: form.name, email: form.email, contact: form.phone },
        tracking: { content_name: `Donation to ${programme.name}`, content_type: "donation", product_type: "donation", programme_id: programme.id },
      });
      const v = await api.post("/donations/verify", {
        razorpay_order_id: resp.razorpay_order_id,
        razorpay_payment_id: resp.razorpay_payment_id,
        razorpay_signature: resp.razorpay_signature,
      });
      setDone({ paid: true, message: v.data.message, amount: v.data.amount || finalAmount, paymentId: resp.razorpay_payment_id, receiptUrl: v.data.receipt_url });
    } catch (err) {
      toast.error(err?.response ? formatApiError(err.response.data?.detail) : err.message || "Could not process the donation.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setDone(null); setOpen(false); };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <PtfButton data-testid="programme-donate-open" className="w-full justify-center">Donate to This Programme</PtfButton>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="programme-donate-modal">
        {done ? (
          <motion.div data-testid="programme-donation-success" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="py-4">
            <span className="grid place-items-center h-14 w-14 rounded-full bg-ptf-accent text-ptf-primary mb-6"><Check className="h-7 w-7" /></span>
            <h3 className="font-display uppercase text-2xl tracking-tight text-ptf-primary">{done.paid ? "Contribution Received" : "Noted, with gratitude."}</h3>
            <p className="mt-4 text-ptf-text/75 leading-relaxed">{done.message}</p>
            {done.paid && (
              <div className="mt-4 text-sm text-ptf-text/70 space-y-1" data-testid="donation-confirmation-details">
                <p>Programme: <strong className="text-ptf-primary">{programme.name}</strong></p>
                <p>Amount: <strong className="text-ptf-primary">₹{Number(done.amount).toLocaleString("en-IN")}</strong></p>
                {done.paymentId && <p className="text-xs text-ptf-text/50">Payment Reference: {done.paymentId}</p>}
                {done.receiptUrl && (
                  <a href={`${process.env.REACT_APP_BACKEND_URL}${done.receiptUrl}`} target="_blank" rel="noreferrer" data-testid="donation-receipt-link"
                     className="inline-block mt-2 text-xs uppercase tracking-[0.14em] underline underline-offset-4 text-ptf-secondary hover:text-ptf-primary">
                    Download Receipt (PDF)
                  </a>
                )}
              </div>
            )}
            <button type="button" onClick={reset} className="mt-8 text-xs uppercase tracking-[0.18em] underline underline-offset-4 text-ptf-primary hover:text-ptf-secondary">Close</button>
          </motion.div>
        ) : (
          <form onSubmit={submit} data-testid="programme-donation-form" className="space-y-6 pt-2">
            <div>
              <DialogTitle className="font-display uppercase text-xl tracking-tight text-ptf-primary flex items-center gap-2">
                <HeartHandshake className="h-5 w-5 text-ptf-secondary" /> {programme.donation_title || "Support This Programme"}
              </DialogTitle>
              <p className="mt-2 text-sm text-ptf-text/70 leading-relaxed">
                {programme.donation_description || `Your contribution supports the work of the ${programme.name} programme.`}
              </p>
            </div>

            <div>
              <p className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-text/60 mb-3">Amount (INR)</p>
              <div className="flex flex-wrap gap-2">
                {presets.map((p) => (
                  <button
                    key={p}
                    type="button"
                    data-testid={`programme-donate-amount-${p}`}
                    onClick={() => { setAmount(p); setCustom(""); }}
                    className={`px-4 py-2.5 text-sm font-medium border transition-colors ${!custom && amount === p ? "bg-ptf-primary text-ptf-bg border-ptf-primary" : "border-ptf-primary/25 text-ptf-text/75 hover:border-ptf-primary"}`}
                  >
                    ₹{p.toLocaleString("en-IN")}
                  </button>
                ))}
                <input
                  type="number"
                  min={minAmount}
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  placeholder="Other ₹"
                  data-testid="programme-donate-amount-custom"
                  className={`w-28 px-3 py-2.5 text-sm border outline-none transition-colors ${custom ? "border-ptf-primary bg-ptf-primary/5" : "border-ptf-primary/25"} focus:border-ptf-secondary`}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <input required data-testid="programme-donate-name" value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name *" />
              <input required type="email" data-testid="programme-donate-email" value={form.email} onChange={set("email")} className={inputCls} placeholder="Email *" />
              <input data-testid="programme-donate-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="Phone (optional)" />
            </div>

            <PtfButton type="submit" disabled={loading} data-testid="programme-donate-submit" className="w-full justify-center">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {loading ? "Processing" : `Donate ₹${(finalAmount || 0).toLocaleString("en-IN")}`}
            </PtfButton>
            <p className="text-[0.65rem] text-ptf-text/50 leading-relaxed">
              Your support helps us develop and deliver this programme. Every donation is acknowledged by email.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
